import api from 'src/store/api'
import dates from 'src/utils/dates'
import http from 'src/store/http'

import { firecollection } from 'src/store/firebase'

import Router from 'src/router/index'

import actionNames from './actionNames'
import User from './utils/user'

const logger = console

const actionMap = {
  organization: {
    CREATE: 'createOrganization',
    READ: 'auth/getOrgData',
    UPDATE: 'updateOrganization',
    DELETE: 'deleteOrganization',
  },
  person: {
    CREATE: 'commitPersonCreate',
    READ: '',
    UPDATE: 'commitPersonUpdate',
    DELETE: 'commitPersonDelete',
  },
  personApi: {
    CREATE: 'createPerson',
    READ: '',
    UPDATE: 'updatePerson',
    DELETE: 'deletePerson',
  },
  project: {
    CREATE: 'createProject',
    READ: '',
    UPDATE: 'updateProject',
    DELETE: 'deleteProject',
  },
  time: {
    READ: '',
  },
}

const getValue = (val) => {
  if (val === undefined) return '(undefined)'

  if (typeof val === 'object') {
    // If the value is null, return '(null)'.
    if (val === null) return '(null)'

    if (val.toDate && typeof val.toDate === 'function') {
      // If the value is a Firestore Timestamp, convert it to a date.
      return dates.getFormattedDate(val.toDate(), 'YYYY-MM-DD HH:mm:ss')
    }

    if (val.toISOString && typeof val.toISOString === 'function') {
      // If the value is a Date object, convert it to a date string.
      return dates.getFormattedDate(val, 'YYYY-MM-DD HH:mm:ss')
    }

    if (Array.isArray(val)) {
      // If the value is an array of objects, handle it accordingly.
      if (val.length === 0) return '[]'

      // If the value is an array of strings, handle it accordingly.
      if (val.every((item) => typeof item === 'string')) {
        // If all items in the array are strings, join them with a comma.
        return `[${val.join(', ')}]`
      }

      // Otherwise, recursively call getValue on each item.
      return `[${val.map((item) => getValue(item)).join(', ')}]`
    }

    // If the value is an object, return it as is.
    return val
  }

  // If the value is a string, number, or boolean, return it as is.
  return val
}

const actions = {

  /**
   * @section Setup
   * @desc This section is for setting up the store. These functions are
   * called by the ToolsStore to set up the store.
   * @see src/store/tools/actions.js
   */

  setup() {},

  async getData({ dispatch }, reportKey) {
    return dispatch('tools/setView', reportKey, { root: true })
  },

  async normalizeLogs() {
    const catcher = (error) => {
      logger.log(`Error normalizing log: ${error.message}`, error)

      return error
    }

    // Get the desired logs from Firestore.

    const filterCriteria = []
    const limitCount = 100
    const sortOrder = firecollection.orderBy('commitTimestamp', 'desc')
    const whereClauses = firecollection.where(filterCriteria)
    const firestoreLogs = await firecollection.findAll('change', whereClauses, limitCount, sortOrder, catcher)

    // Map the logs to the desired format.
    const mappedLogs = firestoreLogs.map((log) => {
      const commitTimestampAsDate = log.commitTimestamp?.toDate()

      const changes = log.changes.reduce((acc, change) => {
        const { valueAfter, valueBefore } = change
        const typeOfValueAfter = typeof valueAfter
        const typeOfValueBefore = typeof valueBefore

        const isObjectAfter = () => typeOfValueAfter === 'object' && valueAfter !== null
        const isObjectBefore = () => typeOfValueBefore === 'object' && valueBefore !== null

        if (typeOfValueAfter !== typeOfValueBefore) {
          acc.push(change)
        } else if (isObjectAfter() && isObjectBefore()) {
          const isEqual = JSON.stringify(valueAfter) === JSON.stringify(valueBefore)

          if (!isEqual) {
            acc.push(change)
          }
        } else if (isObjectAfter() && !isObjectBefore()) {
          acc.push(change)
        } else if (!isObjectAfter() && isObjectBefore()) {
          acc.push(change)
        } else if (valueBefore !== valueAfter) {
          acc.push(change)
        }

        // If the values are equal, do not push the change so the log is cleaner.
        // If the values are not equal, push the change.

        return acc
      }, []).filter((thisLog) => {
        const { committerEmail } = thisLog

        // Filter out logs by internals.
        return /adops.com/.test(committerEmail) === false
      })

      return {
        ...log,
        changes,
        commitTimestampAsDate,
      }
    })

    // Set the logs in Firestore.
    const result = await firecollection.set('change', mappedLogs, catcher)

    if (result.error) {
      logger.log(`Error setting logs in Firestore: ${result.error.message}`, result.error)
      return result.error
    }

    // Log the result.
    logger.log('Firestore Logs:', result)

    // Return the logs.
    return mappedLogs
  },

  async fetchLogsFromFirestore({ rootGetters }) {
    const userEmail = rootGetters['auth/userEmail']
    const isAccessLevelAdmin = rootGetters['auth/isAccessLevelAdmin']

    const catcher = (error) => {
      // Log the error to the console.
      // If the error has an 'error' property, it's already been caught
      // by the Firestore error handler and transformed, so we need to
      // get the error message from one level deeper.
      if (error && error.error && error.error.message) {
        logger.error(`Firestore Error: ${error.error.message}`, error)
      } else {
        logger.error(`Log Error Unknown: ${error.message}`, error)
      }

      // Do not throw the error here, as it will be handled by the caller.
    }

    const filterCriteria = []

    // If the user is not an admin, filter by their email.
    if (!isAccessLevelAdmin) {
      filterCriteria.push(['committerEmail', '==', userEmail])
      filterCriteria.push(['commitCollectionName', '==', 'person'])
    }

    const whereClauses = firecollection.where(filterCriteria)

    const limitCount = 1000
    const sortOrder = firecollection.orderBy('commitTimestamp', 'desc')

    // Get the logs from Firestore.
    const firestoreLogs = await firecollection.findAll('change', whereClauses, limitCount, sortOrder, catcher).catch((error) => error)

    if (!Array.isArray(firestoreLogs)) {
      return firestoreLogs
    }

    // Map the logs to a more readable format.

    const registrationProperties = [
      'isAuthRegistered',
      'registeredAt',
      'registeredAtServerTimestamp',
      'registeredBy',
    ]

    const accessProperties = [
      ...registrationProperties,
      'lastSignInIpAddress',
      'lastSignInLoggedBy',
      'lastSignInServerTimestamp',
      'lastSignInTimestamp',
    ]

    const mappedLogs = firestoreLogs.reduce((logAcc, log) => {
      // Set defaults. This is updated later in the function.
      log.logType = 'activity'
      log.occurrence = 1
      log.logTimestamp = null
      log.logEvent = 'updated'
      log.message = ''

      log.changedProperties = []
      log.actions = []

      const commitTimestamp = dates.getFormattedDate(log.commitTimestamp.toDate(), 'YYYY-MM-DD HH:mm:ss')

      const getDateFromTimestamp = (value) => {
        const dateFunction = value?.toDate
        return dateFunction === 'function' ? dateFunction() : value
      }

      const isObject = (value) => typeof value === 'object' && value !== null

      const findChangedProperties = (oldValue, newValue) => {
        const changedProperties = {}

        if (!isObject(oldValue) || !isObject(newValue)) {
          // If the old value or new value is not an object, return an empty object.
          return changedProperties
        }

        Object.keys(oldValue).forEach((key) => {
          const foundValue = newValue[key]

          if (foundValue !== undefined) {
            changedProperties[key] = foundValue
          }
        })

        return changedProperties
      }

      const changes = log.changes.reduce((changeAcc, change, changeIndex) => {
        const { propertyPath, valueAfter, valueBefore } = change

        const typeOfValueAfter = typeof valueAfter
        const typeOfValueBefore = typeof valueBefore

        const isObjectAfter = isObject(valueAfter)
        const isObjectBefore = isObject(valueBefore)

        // Determine the values before and after the change.
        const foundValueAfter = getValue(valueAfter)
        const foundValueBefore = getValue(valueBefore)

        const formattedValueAfter = foundValueAfter && typeof foundValueAfter === 'object' ? findChangedProperties(foundValueAfter) : foundValueAfter
        const formattedValueBefore = foundValueBefore && typeof foundValueBefore === 'object' ? findChangedProperties(foundValueBefore) : foundValueBefore

        // Store the changes in a more readable format.
        change.index = changeIndex
        change.from = valueBefore === undefined ? '(undefined)' : formattedValueBefore
        change.to = valueAfter === undefined ? '(undefined)' : formattedValueAfter

        const fieldName = propertyPath.join('.')
        const isAccessLog = accessProperties.includes(fieldName)

        // Update the change object for better readability.
        const isRegistrationLog = () => registrationProperties.indexOf(fieldName) > -1
        const getAuthAction = () => (isRegistrationLog() ? 'register' : 'sign in')

        change.action = isAccessLog ? getAuthAction() : 'update'
        change.fieldName = fieldName

        // Determine the change type. Set it on the change object.
        change.logType = isAccessLog ? 'access' : 'activity'

        // Track the change type on the log.
        log.actions.push(change.action)

        // If the change is an access log, get the timestamp and set the log message.
        if (change.logType === 'access') {
          // Set the access timestamp. The type can be a date string or a Firestore Timestamp.
          // We favor this one because it's slightly more accurate than the server timestamp.
          // However, we may want to move it to its own log field in the future.
          const logTimestamp = typeof valueAfter === 'string' ? valueAfter : getDateFromTimestamp()

          // Set the log timestamp to the access log timestamp, formatted.
          log.logTimestamp = dates.getFormattedDate(logTimestamp, 'YYYY-MM-DD HH:mm:ss')

          // Set the action name based on the change type.
          const message = change.action === 'register' ? 'User registered' : `User ${change.action}`
          const messageExists = log.changedProperties.find((prop) => prop === message)

          // If the message does not already exist, add to array of messages.
          // This prevents duplicate messages caused, for example, when two
          // timestamp properties are updated at once on a user record, which
          // occurs when a user signs in.
          if (!messageExists) {
            log.changedProperties.push(message)
          }
        } else if (change.fieldName === 'committerUid') {
          const logTimestamp = typeof valueAfter === 'string' ? valueAfter : getDateFromTimestamp()

          // Set the log timestamp to the access log timestamp, formatted.
          log.logTimestamp = dates.getFormattedDate(logTimestamp, 'YYYY-MM-DD HH:mm:ss')

          // Don't push this change to the changedProperties array, as it is not a user-facing change that needs to be pushed to the table.
        } else {
          // Set the log timestamp to the server's commit timestamp, formatted.
          log.logTimestamp = dates.getFormattedDate(log.commitTimestamp.toDate(), 'YYYY-MM-DD HH:mm:ss')

          // Set the action name based on the change type.
          const message = `${change.action} the ${fieldName} property from ${change.from} to ${change.to}`

          log.changedProperties.push(message)
        }

        // Push the change to the accumulator if the values are different.
        if (typeOfValueAfter !== typeOfValueBefore) {
          changeAcc.push(change)
        } else if (isObjectAfter && isObjectBefore) {
          const isEqual = JSON.stringify(valueAfter) === JSON.stringify(valueBefore)

          if (!isEqual) {
            changeAcc.push(change)
          }
        } else if (isObjectAfter && !isObjectBefore) {
          changeAcc.push(change)
        } else if (!isObjectAfter && isObjectBefore) {
          changeAcc.push(change)
        } else if (valueBefore !== valueAfter) {
          changeAcc.push(change)
        }

        // If the values are equal, do not push the change so the log is cleaner.
        // If the values are not equal, push the change.

        return changeAcc
      }, [])

      // Loop through the changedProperties to determine the log type.
      const logsRegistrationEvent = () => log.actions.find(((action) => action === 'register'))
      const logsSignInEvent = () => log.actions.find(((action) => action === 'sign in'))

      if (logsRegistrationEvent()) {
        log.logType = 'access'
        log.logEvent = 'registered'
      } else if (logsSignInEvent()) {
        log.logType = 'access'
        log.logEvent = 'signed in'
      } else {
        log.logType = 'activity'
        log.logEvent = 'updated'
      }

      logAcc.push({
        ...log,
        changes,
        commitTimestamp,
      })

      return logAcc
    }, [])

    // Return the logs.
    return mappedLogs
  },

  async fetchLogs({ dispatch, rootGetters }) {
    const hasAccessToLogs = rootGetters['auth/hasAccessToLogs']

    // If the user does not have access to logs, return an empty array.
    if (!hasAccessToLogs) {
      return []
    }

    // @todo Create issue: Surface Timeesheet Logs
    // const apiLogs = await api.getChangeHistoryForTimesheets()

    // @todo Create issue: Make request to Google Analytics for events.
    // const gaLogs = await fireanalytics.getEvents()

    // Get logs from Firestore.
    const firestoreLogs = await dispatch('fetchLogsFromFirestore')

    // Return the combined logs.
    return firestoreLogs
  },

  async fetchEnterpriseData({ rootGetters }) {
    // Get the billingCodes set on the user's custom claims.
    const currentUser = rootGetters['auth/currentUser']
    const { domainName, enterpriseId } = currentUser

    // @todo Rename getter to plural 'networks' in auth store.
    // const networks = rootGetters['auth/network']

    const catcher = (error) => {
      logger.log(`Error fetching enterprise data: ${error.message}`, error)

      return error
    }

    // // Get the enterprise data from Firestore.
    const enterprise = await firecollection.get('enterprise', enterpriseId, catcher)

    // Get the domain document from Firestore.
    const domain = await firecollection.get('domain', domainName, catcher)

    return { currentUser, domain, enterprise }
  },

  updateEnterpriseName(context, doc) {
    const { docId } = doc

    // Dispatch the updateEnterprise action.
    return firecollection.set('enterprise', docId, doc, { merge: true })
  },

  /**
   * @section Functions Ported from Internal Store
   * @desc These functions were ported from the internal store to the admin store
   * to be used in the admin dashboard.
   * @see src/internal/store/actions.js
   */

  mapProject(context, project) {
    const { projectCode, projectId, projectName } = project

    return {
      project_code: projectCode,
      project_id: projectId,
      project_name: projectName,
    }
  },
  async onEditProject({ commit, dispatch }, row) {
    const project = await dispatch('mapProject', row)

    commit('selected', project)
    commit('entry', project)

    commit('action', 'update')

    commit('isProjectModalVisible', true)
  },
  async onDeleteProject({ commit, dispatch }, row) {
    const project = await dispatch('mapProject', row)

    commit('selected', project)
    commit('entry', project)

    commit('action', 'delete')

    commit('isProjectModalVisible', true)
  },

  async updateTimeEntries({ commit, dispatch, rootGetters, rootState, state }, { endpointName, moduleName }) {
    const { auth } = rootState
    const { user } = auth

    const reportKey = rootGetters['tools/reportKey']
    const body = {
      person_email: user.email,
      include_delegates: false,
    }

    const response = await api.getEntries(body)
    const { content, error } = response
    const root = { root: true }

    if (!error) {
      const { summaries } = state
      const items = await dispatch('tools/transformTimeItems', content, root)
      const itemsReport = { endpointName, items, moduleName, reportKey, summaries }

      commit('tools/items', itemsReport, root)
      dispatch('tools/summaries', itemsReport, root)

      dispatch('tools/setView', reportKey, root)
    } else {
      const message = error || 'View update erred. Reload to view the latest time entries.'

      commit('tools/snackbar', true, root)
      commit('tools/snack', { message, status: 'error' }, root)
    }

    return response
  },

  async getProject({ dispatch, rootGetters }, reportId) {
    const endpointName = 'project'
    const reportKey = rootGetters['tools/reportKey']
    const root = { root: true }
    const applyFilters = false
    const requestReport = { applyFilters, endpointName, reportKey }

    const handler = await dispatch('tools/requestHandler', requestReport, root)
    const { catcher, success } = handler

    const isReportIdBillingCode = rootGetters['tools/isReportIdBillingCode']
    const request = isReportIdBillingCode
      ? api.getProjectByCode(reportId)
      : api.getProjectById(reportId)

    const response = await request.catch(catcher)
    const { content: items, error } = response

    if (error) return catcher(response)

    const moduleName = 'project'
    const responseReport = { endpointName, items, moduleName, success }

    return dispatch('tools/requestSuccess', responseReport, root)
  },

  async openTimeEntryFormCreate({ commit, state, rootGetters, rootState }, weekdate) {
    const { defaultEntry } = state
    const persons = rootGetters['tools/persons']
    const { user } = rootState.auth

    const defaults = Object.create(defaultEntry)
    const thisPerson = persons.find((person) => person.personEmail === user.email)
    const time_entry_date = dates.getFormattedDate(weekdate, 'YYYY-MM-DD')

    const entry = {
      ...defaults,
      person_id: thisPerson.personId,
      time_entry_date,
      time_classification_id: 'NON-BILLABLE',
    }

    commit('action', 'create')
    commit('weekdate', weekdate)

    commit('selected', entry)
    commit('entry', entry)

    commit('isTimeModalVisible', true)
  },
  openTimeEntryFormUpdate({ commit }, entry) {
    commit('action', 'update')

    commit('selected', entry)
    commit('entry', entry)

    commit('isTimeModalVisible', true)
  },
  openTimeEntryFormDelete({ commit }, entry) {
    commit('action', 'delete')

    commit('selected', entry)
    commit('entry', entry)

    commit('isTimeModalVisible', true)
  },

  createTimeEntry({ commit }, entry) {
    entry.time_entry_date = dates.getFormattedDate(entry.time_entry_date, 'YYYY-MM-DD')

    return api.createTimeEntry(entry).catch((error) => {
      commit(actionNames.ADD_TIME_FAILED, error)
    })
  },
  updateTimeEntry({ commit, rootState }, entry) {
    const { auth } = rootState
    const { user } = auth
    const { email: person_email } = user

    const data = {
      client_id: entry.client_id,
      description: entry.description,
      duration: entry.duration,
      person_email,
      project_id: entry.project_id,
      service_id: entry.service_id,
      time_classification_id: entry.time_classification_id,
      time_entry_date: dates.getFormattedDate(entry.time_entry_date, 'MM-DD-YYYY'),
      time_entry_id: entry.timeEntryId,
    }

    return api.updateTimeEntry(data).catch((error) => {
      commit(actionNames.EDIT_TIME_FAILED, error)
    })
  },
  deleteTimeEntry({ commit, rootState }, entry) {
    const { auth } = rootState
    const { user } = auth
    const { email: person_email } = user
    const { timeEntryId: time_entry_id } = entry

    const data = {
      person_email,
      time_entry_id,
    }

    return api.deleteTimeEntry(data).catch((error) => {
      commit(actionNames.DELETE_TIME_FAILED, error)
    })
  },
  async submitTimeEntry({ dispatch, rootGetters, state }, entry) {
    const reportKey = rootGetters['tools/reportKey']
    const { action } = state
    let items

    if (action === 'create') {
      items = await dispatch('createTimeEntry', entry)
    }
    if (action === 'update') {
      items = await dispatch('updateTimeEntry', entry)
    }
    if (action === 'delete') {
      items = await dispatch('deleteTimeEntry', entry)
    }

    dispatch('updateTimeEntries', { endpointName: 'times', moduleName: reportKey })

    return items
  },

  createItem({ dispatch, rootState }, weekdate) {
    const { tools } = rootState
    const { reportName } = tools

    if (reportName === 'organization') {
      return dispatch('createOrganizationForm')
    }

    if (reportName === 'person') {
      return dispatch('createPersonForm')
    }

    if (reportName === 'project') {
      return dispatch('createProjectForm')
    }

    return dispatch('openTimeEntryFormCreate', weekdate)
  },

  showFormOrganization({ commit, dispatch }, { action = 'create', formData }) {
    dispatch('setFormData', { action, formData })
    commit('isOrganizationModalVisible', true)
  },
  showFormOrganizationUpdate({ dispatch }, formData) {
    dispatch('showFormOrganization', { action: 'update', formData })
  },
  showFormOrganizationDelete({ dispatch }, formData) {
    dispatch('showFormOrganization', { action: 'delete', formData })
  },
  goToOrganization(context, organization) {
    const { billingCode } = organization
    Router.push(`/internal/organization/${billingCode}`)
  },

  mapOrgToClient(context, organization) {
    const {
      action_type,
      billingCode,
      clientId,
      clientName,
      publisherName,
      isAdministrative,
    } = organization

    return {
      action_type,
      billing_code: billingCode,
      client_id: clientId,
      client_name: clientName || publisherName,
      is_administrative: isAdministrative,
    }
  },
  mapEntryToClient(context, organization) {
    const {
      actionType,
      billingCode,
      clientId,
      clientName,
      isAdministrative,
    } = organization

    return {
      action_type: actionType,
      billing_code: billingCode,
      client_id: clientId,
      client_name: clientName,
      is_administrative: isAdministrative,
    }
  },
  async createOrganizationForm({ dispatch, state }) {
    const { defaultOrg } = state
    const formData = await dispatch('mapOrgToClient', defaultOrg)

    dispatch('showFormOrganization', { action: 'create', formData })
  },
  async modifyOrganization({ commit, dispatch, getters, rootState }, { events, request }) {
    const { reportKey } = getters
    const { succeeded } = events
    const root = { root: true }

    const catcher = (err) => {
      const error = http.catch(err)
      dispatch('messages/showErrorMessage', { error, reportKey }, root)
      return error
    }
    const response = await api.modifyOrganization(request).catch(catcher)
    const { content, error } = response

    if (error) {
      catcher(error)
    } else {
      const { user } = rootState.auth
      const moduleName = 'organization'

      dispatch('auth/getOrganizations', { moduleName, user }, { root: true })

      commit(succeeded, content)
    }

    return response
  },
  async createOrganization({ dispatch, getters }, organization) {
    const { reportKey } = getters
    const root = { root: true }
    dispatch('messages/showLoadingMessage', { reportKey, text: 'Requesting data ...' }, root)

    const request = await dispatch('mapEntryToClient', organization)

    const failed = actionNames.ADD_ORGANIZATION_FAILED
    const succeeded = actionNames.ADD_ORGANIZATION_SUCCEEDED

    const events = { failed, succeeded }
    const modify = { events, request }

    return dispatch('modifyOrganization', modify)
  },
  async updateOrganization({ commit, dispatch }, organization) {
    commit(actionNames.EDIT_ORGANIZATION_REQUESTED)

    const request = await dispatch('mapEntryToClient', organization)

    const failed = actionNames.EDIT_ORGANIZATION_FAILED
    const succeeded = actionNames.EDIT_ORGANIZATION_SUCCEEDED

    const events = { failed, succeeded }
    const modify = { events, request }

    return dispatch('modifyOrganization', modify)
  },
  async deleteOrganization({ commit, dispatch }, organization) {
    commit(actionNames.DELETE_ORGANIZATION_REQUESTED)

    const { actionType, clientId } = organization
    const request = { action_type: actionType, client_id: clientId }

    const failed = actionNames.DELETE_ORGANIZATION_FAILED
    const succeeded = actionNames.DELETE_ORGANIZATION_SUCCEEDED

    const events = { failed, succeeded }
    const modify = { events, request }

    return dispatch('modifyOrganization', modify)
  },

  setFormData({ commit }, { action, formData }) {
    commit('selected', { ...formData })
    commit('entry', formData)
    commit('action', action)
  },

  showFormPerson({ commit, dispatch }, { action = 'create', formData }) {
    dispatch('setFormData', { action, formData })
    commit('isPersonModalVisible', true)
  },
  async showFormPersonCopy({ dispatch, rootState, state }, copyData) {
    const { defaultPerson, defaultUser } = state
    const { searchData } = rootState.tools
    const defaultPersonUser = { ...defaultPerson, ...defaultUser }
    const organizationsResponse = await searchData.organizations
    const { content: organizations = [] } = organizationsResponse || {}

    const personUser = {
      ...copyData,
      docId: defaultPersonUser.docId,
      email: defaultPersonUser.email,
      personId: defaultPersonUser.personId,
      personNameFirst: defaultPersonUser.personNameFirst,
      personNameLast: defaultPersonUser.personNameLast,
      personEmail: defaultPersonUser.personEmail,
    }

    const formData = await User.mapPersonAndUser(personUser, organizations)

    dispatch('showFormPerson', { action: 'create', formData })
  },
  async showFormPersonCreate({ dispatch, state }) {
    const { defaultPerson, defaultUser } = state
    const personUser = { ...defaultPerson, ...defaultUser }
    const organizations = []
    const formData = await User.mapPersonAndUser(personUser, organizations)

    return dispatch('showFormPerson', { action: 'create', formData })
  },
  showFormPersonUpdate({ dispatch }, formData) {
    dispatch('showFormPerson', { action: 'update', formData })
  },
  showFormPersonDelete({ dispatch }, formData) {
    dispatch('showFormPerson', { action: 'delete', formData })
  },
  goToPerson(context, person) {
    const { docId } = person
    Router.push(`/internal/person/${docId}`)
  },

  mapPerson(context, personUser) {
    const {
      action_type,
      access = {},
      managerPersonId,
      email = '',
      personId: person_id,
      personNameFirst = '',
      personNameLast = '',
    } = personUser
    const { active = false, level, role } = access
    const is_admin = level === 'admin'
    const is_internal = role === 'internal'
    const manager_person_id = (is_internal && managerPersonId) || undefined
    const apiActionTypes = {
      CREATE: 'ADD',
      UPDATE: 'UPDATE',
      DELETE: 'DELETE',
    }

    const email_person = (email || '').toLowerCase().trim()
    const person_name_first = (personNameFirst || '').trim()
    const person_name_last = (personNameLast || '').trim()

    const user = {
      action_type: apiActionTypes[action_type],
      email_person,
      is_active: active,
      is_admin,
      is_internal,
      manager_person_id,
      person_name_first,
      person_name_last,
    }

    if (person_id !== email_person) {
      user.person_id = person_id
    }

    return user
  },

  async createPersonForm({ dispatch }) {
    return dispatch('showFormPersonCreate')
  },
  async upsertPersonAndUser({ dispatch, rootGetters }, data) {
    const { action, catcher, person } = data
    const hasRightsToUpdate = rootGetters['auth/isInternalWriter']

    if (!hasRightsToUpdate) {
      const metadata = { email: person.email }
      const error = { code: '403', layer: 'app', message: 'Forbidden', metadata }
      return catcher({ error })
    }

    const setUserForFirebase = dispatch('auth/upsertUser', person, { root: true })

    const updateApiPerson = () => {
      const missingRequirements = !person.person_id && action === 'update'
      const safeAction = missingRequirements ? 'create' : action
      const actionRequest = { action: safeAction, catcher, person }
      const crudAction = action.toUpperCase()
      const actionName = actionMap.personApi[crudAction]

      return dispatch(actionName, actionRequest)
    }
    const setPersonForApi = updateApiPerson()

    const [apiRes] = await Promise.all([setPersonForApi, setUserForFirebase])
    const { content, error } = apiRes || {}

    if (error) catcher(apiRes)

    const [apiPerson] = Array.isArray(content) ? content : [content]
    const user = await firecollection.get('person', person.email)

    return { error, person: apiPerson, user }
  },

  // Differs from the internal store, as the action does not require a catcher.
  async createPerson({ dispatch }, person) {
    const request = await dispatch('mapPerson', person)

    return api.modifyPerson(request)
  },
  // Differs from the internal store, as the action does not require a catcher.
  async updatePerson({ dispatch }, person) {
    const request = await dispatch('mapPerson', person)

    return api.modifyPerson(request)
  },
  // Differs from the internal store, as the action does not require a catcher.
  async deletePerson(context, person) {
    const { action_type, personId: person_id } = person

    const request = {
      action_type,
      person_id,
    }

    return api.modifyPerson(request)
  },

  async commitPersonCreate({ commit, dispatch }, person) {
    commit(actionNames.ADD_PERSON_REQUESTED)

    const catcher = (caughtError) => {
      const error = http.catch(caughtError)
      commit(actionNames.ADD_PERSON_FAILED, error)
      return error
    }
    const action = 'CREATE'
    const request = { action, catcher, person }

    const personUser = await dispatch('upsertPersonAndUser', request)
    const { error: personError, person: apiPerson, user } = personUser

    if (personError) return catcher(personUser)

    const content = apiPerson ? { ...apiPerson, ...user } : user

    commit(actionNames.ADD_PERSON_SUCCEEDED, content)

    const reportKey = 'person'

    dispatch('tools/setView', reportKey, { root: true })

    return content
  },
  async commitPersonUpdate({ commit, dispatch }, person) {
    commit(actionNames.EDIT_PERSON_REQUESTED)

    const catcher = (err) => {
      const error = http.catch(err)
      commit(actionNames.EDIT_PERSON_FAILED, error)
      return error
    }
    const action = 'UPDATE'
    const request = { action, catcher, person }

    const personUser = await dispatch('upsertPersonAndUser', request)
    const { error, person: apiPerson, user } = personUser

    if (error) return catcher(personUser)

    const content = apiPerson ? { ...apiPerson, ...user } : user
    const reportKey = 'person'

    dispatch('tools/setView', reportKey, { root: true })

    commit(actionNames.EDIT_PERSON_SUCCEEDED, content)

    // Different from the internal store, we do not return the person here.
    // Instead, we return the response from the API call.
    return personUser
  },
  async commitPersonDelete({ commit, dispatch }, person) {
    commit(actionNames.DELETE_PERSON_REQUESTED)

    const catcher = (err) => {
      const error = http.catch(err)
      commit(actionNames.DELETE_PERSON_FAILED, error)
      return error
    }
    const action = 'DELETE'
    const request = { action, catcher, person }

    const personUser = await dispatch('upsertPersonAndUser', request)
    const { error, person: apiPerson, user } = personUser

    if (error) return catcher(personUser)

    const content = { ...apiPerson, ...user }
    const reportKey = 'person'

    dispatch('tools/setView', reportKey, { root: true })

    commit(actionNames.DELETE_PERSON_SUCCEEDED, content)

    return content
  },

  async updateUser({ dispatch, rootGetters }, data) {
    const { catcher, person } = data
    const hasRightsToUpdate = rootGetters['auth/isUserInternalAdmin']

    if (!hasRightsToUpdate) {
      const metadata = { email: person.email }
      const error = { code: '403', layer: 'app', message: 'Forbidden', metadata }
      return catcher({ error })
    }

    await dispatch('auth/upsertUser', person, { root: true })

    return firecollection.get('person', person.email)
  },

  showFormProject({ commit, dispatch }, { action = 'create', formData }) {
    dispatch('setFormData', { action, formData })
    commit('isProjectModalVisible', true)
  },
  showFormProjectUpdate({ dispatch }, formData) {
    dispatch('showFormProject', { action: 'update', formData })
  },
  showFormProjectDelete({ dispatch }, formData) {
    dispatch('showFormProject', { action: 'delete', formData })
  },

  async createProjectForm({ dispatch, state }) {
    const { defaultProject } = state
    const formData = await dispatch('mapProject', defaultProject)

    dispatch('showFormProject', { action: 'create', formData })
  },
  async modifyProject({ commit, dispatch, rootGetters }, { events, request }) {
    const { failed, succeeded } = events

    const catcher = (err) => {
      const error = http.catch(err)
      commit(failed, error)
      return error
    }
    const response = await api.modifyProject(request).catch(catcher)
    const { content, error } = response

    if (error) {
      catcher(error)
    } else {
      const reportKey = rootGetters['tools/reportKey']
      const columnsByKey = rootGetters['tools/columnsByKey']

      dispatch('setProjectView', { columnsByKey, params: { reportKey } })

      commit(succeeded, content)
    }

    return response
  },
  async createProject({ commit, dispatch }, project) {
    commit(actionNames.ADD_PROJECT_REQUESTED)

    const { action_type, project_code, project_name } = project
    const request = { action_type, project_code, project_name }
    const failed = actionNames.ADD_PROJECT_FAILED
    const succeeded = actionNames.ADD_PROJECT_SUCCEEDED
    const events = { failed, succeeded }
    const modify = { events, request }

    return dispatch('modifyProject', modify)
  },
  async updateProject({ commit, dispatch }, project) {
    commit(actionNames.EDIT_PROJECT_REQUESTED)

    const { action_type, project_id, project_code, project_name } = project
    const request = { action_type, project_id, project_code, project_name }
    const failed = actionNames.EDIT_PROJECT_FAILED
    const succeeded = actionNames.EDIT_PROJECT_SUCCEEDED
    const events = { failed, succeeded }
    const modify = { events, request }

    return dispatch('modifyProject', modify)
  },
  async deleteProject({ commit, dispatch }, project) {
    commit(actionNames.DELETE_PROJECT_REQUESTED)

    const { action_type, project_id } = project
    const request = { action_type, project_id }
    const failed = actionNames.DELETE_PROJECT_FAILED
    const succeeded = actionNames.DELETE_PROJECT_SUCCEEDED
    const events = { failed, succeeded }
    const modify = { events, request }

    return dispatch('modifyProject', modify)
  },

  async submitAction({ commit, dispatch }, actionRequest) {
    const { action, endpointName, value } = actionRequest
    const currentAction = actionMap[endpointName][action.toUpperCase()]

    if (!currentAction) {
      commit(actionNames.UNKNOWN_ERROR, 'action')

      return Promise.resolve(actionNames.UNKNOWN_ERROR)
    }

    return dispatch(currentAction, value)
  },
  submitOrgAction({ dispatch, state }, organization) {
    const { action } = state
    const endpointName = 'organization'
    const actionRequest = { action, endpointName, value: organization }

    return dispatch('submitAction', actionRequest)
  },
  async submitPersonAction({ dispatch, state }, value) {
    const { action } = state
    const endpointName = 'person'
    const actionRequest = { action, endpointName, value }

    return dispatch('submitAction', actionRequest)
  },
  submitProjectAction({ dispatch, state }, project) {
    const { action } = state
    const endpointName = 'project'
    const actionRequest = { action, endpointName, value: project }

    return dispatch('submitAction', actionRequest)
  },
}

export default actions
